import { useSyncExternalStore } from 'react';
import { storage } from '../../../services/StorageService';

/**
 * Settings → Interface "Hotbar cooldown numbers" (UserPrefs
 * ui.hotbarCooldownNumbers, default on). One cached value and one prefs
 * subscription for the whole bar; slots read it with useSyncExternalStore, so
 * a toggle re-renders the slots once and nothing polls.
 */

let cached: boolean | null = null;
const listeners = new Set<() => void>();
let unsubscribeStorage: (() => void) | null = null;

function read(): boolean {
  if (cached === null) {
    try {
      cached = storage.loadUserPrefs().ui.hotbarCooldownNumbers;
    } catch {
      cached = true; // storage unavailable (tests, private window): keep the default
    }
  }
  return cached;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!unsubscribeStorage) {
    try {
      unsubscribeStorage = storage.subscribeUserPrefs((prefs) => {
        const next = prefs.ui.hotbarCooldownNumbers;
        if (next === cached) return;
        cached = next;
        listeners.forEach((l) => l());
      });
    } catch { /* no storage: the default stands */ }
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && unsubscribeStorage) {
      unsubscribeStorage();
      unsubscribeStorage = null;
      cached = null;
    }
  };
}

export function useHotbarCooldownNumbers(): boolean {
  return useSyncExternalStore(subscribe, read, read);
}

/** Flip the setting (Settings → Interface). */
export function toggleHotbarCooldownNumbers(): void {
  const cur = storage.loadUserPrefs();
  storage.saveUserPrefs({ ...cur, ui: { ...cur.ui, hotbarCooldownNumbers: !cur.ui.hotbarCooldownNumbers } });
}
